import { Panel } from '@xyflow/react';
import { css } from 'aphrodite';
import { useEffect, useRef } from 'react';
import { brandPrimary } from '../../../styles/colors';
import type { NodeSearchResult, NodeSearchState } from '../hooks/useNodeSearch';
import { getStepTypeBorderColor, TRIGGER_INDICATOR_COLOR } from '../utils/stepTypeColors';
import { styleSheet as styles } from './styles/NodeSearchPanelStyles';

interface NodeSearchPanelProps {
	/** Current search state */
	state: NodeSearchState;
	/** Callback when search query changes */
	onSearch: (query: string) => void;
	/** Callback when user selects a result to navigate to */
	onNavigate: (nodeId: string) => void;
	/** Callback to close the search panel */
	onClose: () => void;
}

/**
 * A search panel component for finding and navigating to nodes in the automation flow canvas.
 *
 * Features:
 * - Auto-focuses input when opened
 * - Keyboard navigation (Enter to select first result, Escape to close)
 * - Visual indicators showing node type colors
 * - Displays up to 20 matching results
 *
 * @example
 * ```tsx
 * <NodeSearchPanel
 *   state={searchState}
 *   onSearch={updateQuery}
 *   onNavigate={navigateToNode}
 *   onClose={closeSearch}
 * />
 * ```
 */
export function NodeSearchPanel({
	state,
	onSearch,
	onNavigate,
	onClose,
}: NodeSearchPanelProps): React.ReactElement | null {
	const inputRef = useRef<HTMLInputElement>(null);

	// Auto-focus input when panel opens
	useEffect(() => {
		if (state.isOpen) {
			// Small delay to ensure DOM is ready
			const timer = setTimeout(() => {
				inputRef.current?.focus();
			}, 50);
			return () => clearTimeout(timer);
		}
	}, [state.isOpen]);

	// Don't render if not open
	if (!state.isOpen) {
		return null;
	}

	const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
		if (e.key === 'Escape') {
			e.preventDefault();
			onClose();
		} else if (e.key === 'Enter' && state.results.length > 0) {
			e.preventDefault();
			onNavigate(state.results[0].nodeId);
		}
	};

	return (
		<Panel position='top-center' className={css(styles.panel)}>
			<div className={css(styles.container)}>
				<div className={css(styles.inputWrapper)}>
					<SearchIcon />
					<input
						ref={inputRef}
						type='text'
						className={css(styles.input)}
						placeholder='Search steps by type or name...'
						value={state.query}
						onChange={e => onSearch(e.target.value)}
						onKeyDown={handleKeyDown}
						aria-label='Search automation steps'
						autoComplete='off'
					/>
					{state.query && (
						<button
							type='button'
							className={css(styles.clearButton)}
							onClick={() => onSearch('')}
							aria-label='Clear search'
						>
							<CloseIcon />
						</button>
					)}
				</div>
				{state.results.length > 0 && (
					<div className={css(styles.results)} role='listbox' aria-label='Search results'>
						{state.results.map((result, index) => (
							<button
								key={result.nodeId}
								type='button'
								className={css(styles.resultItem)}
								onClick={() => onNavigate(result.nodeId)}
								role='option'
								aria-selected={index === 0}
							>
								<span className={css(styles.colorIndicator)} style={{ backgroundColor: getResultColor(result) }} />
								<span className={css(styles.resultTitle)}>{result.title}</span>
								<span className={css(styles.resultType)}>{result.nodeType}</span>
							</button>
						))}
					</div>
				)}
				{state.query && state.results.length === 0 && (
					<div className={css(styles.noResults)}>No matching steps found</div>
				)}
				<div className={css(styles.hint)}>
					<kbd className={css(styles.kbd)}>Enter</kbd> to navigate
					<span className={css(styles.hintSeparator)}>·</span>
					<kbd className={css(styles.kbd)}>Esc</kbd> to close
				</div>
			</div>
		</Panel>
	);
}

/**
 * Get the color indicator for a search result based on its type.
 */
function getResultColor(result: NodeSearchResult): string {
	if (result.stepType) {
		return getStepTypeBorderColor(result.stepType);
	}
	if (result.nodeType === 'Trigger') {
		return TRIGGER_INDICATOR_COLOR;
	}
	return brandPrimary;
}

function SearchIcon(): React.ReactElement {
	return (
		<svg className={css(styles.searchIcon)} width='16' height='16' viewBox='0 0 16 16' fill='none'>
			<path
				d='M7 12A5 5 0 107 2a5 5 0 000 10zM14 14l-3.5-3.5'
				stroke='currentColor'
				strokeWidth='1.5'
				strokeLinecap='round'
				strokeLinejoin='round'
			/>
		</svg>
	);
}

function CloseIcon(): React.ReactElement {
	return (
		<svg width='14' height='14' viewBox='0 0 14 14' fill='none'>
			<path d='M3 3l8 8M11 3l-8 8' stroke='currentColor' strokeWidth='1.5' strokeLinecap='round' />
		</svg>
	);
}
